import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { apiFetch, ApiError } from "@/lib/api";
import type { DocumentKind } from "@/lib/documents";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintTrigger, HoverHintWithGlyph, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Input } from "@/components/ui/Input";
import { DocumentModal } from "./DocumentModal";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

const MANAGE_QUERY_KEY = ["document-kinds", "manage"] as const;
const DOCUMENTS_LOOKUPS_QUERY_KEY = ["documents", "lookups"] as const;

const documentsHubSettingsAdminTitle =
  "Module administrator access is required to change Document Control hub settings.";

function DocumentKindEditRow(props: {
  kind: DocumentKind;
  disabled: boolean;
  onPatch: (input: {
    code: string;
    label: string;
    hint: string | null;
    abbreviation: string | null;
    sort_order: number;
    active: boolean;
  }) => Promise<void>;
  isPending: boolean;
  onDelete: (code: string) => void;
  deletePending: boolean;
}) {
  const { kind, disabled, onPatch, isPending, onDelete, deletePending } = props;
  const [label, setLabel] = useState(kind.label);
  const [hint, setHint] = useState(kind.hint ?? "");
  const [abbreviation, setAbbreviation] = useState(kind.abbreviation ?? "");
  const [sortOrder, setSortOrder] = useState(String(kind.sort_order));
  const [active, setActive] = useState(kind.active);

  useEffect(() => {
    setLabel(kind.label);
    setHint(kind.hint ?? "");
    setAbbreviation(kind.abbreviation ?? "");
    setSortOrder(String(kind.sort_order));
    setActive(kind.active);
  }, [kind]);

  const dirty =
    label !== kind.label ||
    (hint.trim() || null) !== (kind.hint ?? null) ||
    (abbreviation.trim().toUpperCase() || null) !== (kind.abbreviation ?? null) ||
    Number.parseInt(sortOrder, 10) !== kind.sort_order ||
    active !== kind.active;

  return (
    <tr className="align-top">
      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-700">{kind.code}</td>
      <td className="min-w-[10rem] px-3 py-2">
        <Input value={label} disabled={disabled} onChange={(event) => setLabel(event.target.value)} />
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        <Input
          className="w-24 font-mono"
          value={abbreviation}
          disabled={disabled}
          onChange={(event) => setAbbreviation(event.target.value.toUpperCase())}
        />
      </td>
      <td className="min-w-[14rem] px-3 py-2">
        <textarea
          className="min-h-16 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 shadow-sm disabled:bg-slate-50"
          value={hint}
          disabled={disabled}
          onChange={(event) => setHint(event.target.value)}
          rows={2}
        />
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        <Input
          className="w-20"
          inputMode="numeric"
          value={sortOrder}
          disabled={disabled}
          onChange={(event) => setSortOrder(event.target.value.replace(/[^\d-]/g, ""))}
        />
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        <input type="checkbox" checked={active} disabled={disabled} onChange={(event) => setActive(event.target.checked)} />
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        <div className="flex flex-wrap gap-2">
          <ReadOnlyHoverLayer active={disabled} hoverHint={documentsHubSettingsAdminTitle}>
            <HoverHintTrigger
              trigger={
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={disabled || isPending || !dirty || label.trim() === ""}
                  onClick={() => {
                    const parsedSort = Number.parseInt(sortOrder, 10);
                    if (!Number.isInteger(parsedSort)) {
                      window.alert("Sort order must be a whole number.");
                      return;
                    }
                    void onPatch({
                      code: kind.code,
                      label: label.trim(),
                      hint: hint.trim() === "" ? null : hint.trim(),
                      abbreviation: abbreviation.trim() === "" ? null : abbreviation.trim().toUpperCase(),
                      sort_order: parsedSort,
                      active,
                    });
                  }}
                >
                  Save
                </Button>
              }
            >
              Write label, hint, sort order, and active flag for this document type code.
            </HoverHintTrigger>
          </ReadOnlyHoverLayer>
          <ReadOnlyHoverLayer active={disabled} hoverHint={documentsHubSettingsAdminTitle}>
            <HoverHintTrigger
              trigger={
                <Button
                  type="button"
                  size="sm"
                  variant="danger"
                  disabled={disabled || deletePending || isPending}
                  onClick={() => onDelete(kind.code)}
                >
                  Delete
                </Button>
              }
            >
              Remove this type only if no document revisions reference it (otherwise the server rejects the delete).
            </HoverHintTrigger>
          </ReadOnlyHoverLayer>
        </div>
      </td>
    </tr>
  );
}

export function DocumentKindsSettingsSection() {
  const queryClient = useQueryClient();
  const hubDocumentSettingsAllowed = useHubModuleAdmin("documents");
  const [addOpen, setAddOpen] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newHint, setNewHint] = useState("");
  const [newAbbreviation, setNewAbbreviation] = useState("");
  const [newSortOrder, setNewSortOrder] = useState("100");
  const [newActive, setNewActive] = useState(true);

  const kindsQuery = useQuery<{ kinds: DocumentKind[] }, ApiError>({
    queryKey: MANAGE_QUERY_KEY,
    queryFn: () => apiFetch<{ kinds: DocumentKind[] }>("/api/document-kinds/manage"),
    enabled: hubDocumentSettingsAllowed,
  });

  const patchMutation = useMutation({
    mutationFn: (input: {
      code: string;
      label: string;
      hint: string | null;
      abbreviation: string | null;
      sort_order: number;
      active: boolean;
    }) =>
      apiFetch<{ kind: DocumentKind }>(`/api/document-kinds/${encodeURIComponent(input.code)}`, {
        method: "PATCH",
        body: {
          label: input.label,
          hint: input.hint,
          abbreviation: input.abbreviation,
          sort_order: input.sort_order,
          active: input.active,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MANAGE_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_LOOKUPS_QUERY_KEY });
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ kind: DocumentKind }>("/api/document-kinds", {
        method: "POST",
        body: {
          code: newCode.trim().toLowerCase(),
          label: newLabel.trim(),
          hint: newHint.trim() === "" ? null : newHint.trim(),
          abbreviation: newAbbreviation.trim() === "" ? null : newAbbreviation.trim().toUpperCase(),
          sort_order: Number.parseInt(newSortOrder, 10),
          active: newActive,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MANAGE_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_LOOKUPS_QUERY_KEY });
      setAddOpen(false);
      setNewCode("");
      setNewLabel("");
      setNewHint("");
      setNewAbbreviation("");
      setNewSortOrder("100");
      setNewActive(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (code: string) =>
      apiFetch<{ ok: true }>(`/api/document-kinds/${encodeURIComponent(code)}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MANAGE_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_LOOKUPS_QUERY_KEY });
    },
  });

  function requestDelete(code: string): void {
    if (
      !window.confirm(
        `Delete document type "${code}"? This only succeeds if no controlled document revisions use this type.`,
      )
    ) {
      return;
    }
    deleteMutation.mutate(code);
  }

  return (
    <section className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Document types</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm text-slate-600">
            Codes are stable identifiers in the database. Operators see the label and hub hover hint in the workbench.
            Lower sort order values list first in dropdowns.
          </p>
          {!hubDocumentSettingsAllowed ? (
            <p className="text-sm text-slate-600">{documentsHubSettingsAdminTitle}</p>
          ) : kindsQuery.isError ? (
            <p className="text-sm text-red-600">{kindsQuery.error.message}</p>
          ) : kindsQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading document types…</p>
          ) : (
            <ReadOnlyHoverLayer active={!hubDocumentSettingsAllowed} hoverHint={documentsHubSettingsAdminTitle} layout="block">
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <th className="px-3 py-2">Code</th>
                      <th className="px-3 py-2">Label</th>
                      <th className="px-3 py-2">Abbreviation</th>
                      <th className="px-3 py-2">Hint</th>
                      <th className="px-3 py-2">Sort</th>
                      <th className="px-3 py-2">Active</th>
                      <th className="px-3 py-2" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {(kindsQuery.data?.kinds ?? []).map((kind) => (
                      <DocumentKindEditRow
                        key={kind.code}
                        kind={kind}
                        disabled={!hubDocumentSettingsAllowed}
                        isPending={patchMutation.isPending}
                        deletePending={deleteMutation.isPending}
                        onPatch={async (input) => {
                          await patchMutation.mutateAsync(input);
                        }}
                        onDelete={requestDelete}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <ReadOnlyHoverLayer active={!hubDocumentSettingsAllowed} hoverHint={documentsHubSettingsAdminTitle}>
                  <HoverHintTrigger
                    trigger={
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={!hubDocumentSettingsAllowed}
                        onClick={() => setAddOpen(true)}
                      >
                        Add document type
                      </Button>
                    }
                  >
                    Create a new type code for future uploads. Use lowercase letters, digits, and underscores only.
                  </HoverHintTrigger>
                </ReadOnlyHoverLayer>
              </div>
            </ReadOnlyHoverLayer>
          )}
          {patchMutation.isError ? (
            <p className="text-sm text-red-600">{(patchMutation.error as Error).message}</p>
          ) : null}
          {createMutation.isError ? (
            <p className="text-sm text-red-600">{(createMutation.error as Error).message}</p>
          ) : null}
          {deleteMutation.isError ? (
            <p className="text-sm text-red-600">{(deleteMutation.error as Error).message}</p>
          ) : null}
        </CardBody>
      </Card>

      {addOpen ? (
        <DocumentModal title="Add document type" onClose={() => setAddOpen(false)} showCloseButton={false}>
          <div className="space-y-4">
            <ReadOnlyHoverLayer active={!hubDocumentSettingsAllowed} hoverHint={documentsHubSettingsAdminTitle} layout="block">
              <fieldset disabled={!hubDocumentSettingsAllowed || createMutation.isPending} className="space-y-3 border-0 p-0">
                <label className="block text-sm font-medium text-slate-700">
                  Code
                  <Input
                    className="mt-1 font-mono"
                    value={newCode}
                    onChange={(event) => setNewCode(event.target.value)}
                    placeholder="e.g. assembly_drawing"
                  />
                  <span className="mt-1 block text-xs text-slate-500">Lowercase letters, digits, and underscores. Cannot be changed later.</span>
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Label
                  <Input className="mt-1" value={newLabel} onChange={(event) => setNewLabel(event.target.value)} />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  <span className="flex items-center gap-1">
                    Abbreviation
                    <HoverHintWithGlyph
                      size="sm"
                      hoverHint="Used in desktop file names: sku.revision_status_abbreviation_title. Letters and digits, up to 12 characters."
                    />
                  </span>
                  <Input
                    className="mt-1 w-28 font-mono"
                    value={newAbbreviation}
                    onChange={(event) => setNewAbbreviation(event.target.value.toUpperCase())}
                    placeholder="2D"
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Hint
                  <textarea
                    className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
                    rows={3}
                    value={newHint}
                    onChange={(event) => setNewHint(event.target.value)}
                  />
                </label>
                <label className="block text-sm font-medium text-slate-700">
                  Sort order
                  <Input
                    className="mt-1 w-28"
                    inputMode="numeric"
                    value={newSortOrder}
                    onChange={(event) => setNewSortOrder(event.target.value.replace(/[^\d-]/g, ""))}
                  />
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={newActive} onChange={(event) => setNewActive(event.target.checked)} />
                  Active (shown in workbench dropdowns)
                </label>
              </fieldset>
            </ReadOnlyHoverLayer>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" disabled={createMutation.isPending} onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <ReadOnlyHoverLayer active={!hubDocumentSettingsAllowed} hoverHint={documentsHubSettingsAdminTitle}>
                <Button
                  type="button"
                  variant="primary"
                  disabled={
                    createMutation.isPending ||
                    !hubDocumentSettingsAllowed ||
                    newCode.trim() === "" ||
                    newLabel.trim() === ""
                  }
                  onClick={() => {
                    const parsedSort = Number.parseInt(newSortOrder, 10);
                    if (!Number.isInteger(parsedSort)) {
                      window.alert("Sort order must be a whole number.");
                      return;
                    }
                    if (!/^[a-z][a-z0-9_]{0,62}$/.test(newCode.trim().toLowerCase())) {
                      window.alert(
                        "Code must start with a letter and use only lowercase letters, digits, and underscores.",
                      );
                      return;
                    }
                    createMutation.mutate();
                  }}
                >
                  Create
                </Button>
              </ReadOnlyHoverLayer>
            </div>
          </div>
        </DocumentModal>
      ) : null}
    </section>
  );
}
